import { Injectable, OnDestroy, Renderer2, RendererFactory2, SecurityContext } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, ValidatorFn, Validators } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { TranslocoService } from '@jsverse/transloco';
import { select, Store } from '@ngrx/store';
import { AddonPermissionService } from 'app/addon-permission/addon-permission.service';
import { AppInjector } from 'app/app.module';
import { AppService } from 'app/app.service';
import { constants } from 'app/constants/constants';
import { Block } from 'app/interfaces/blocks';
import { DroplistOptions, Field } from 'app/interfaces/fields';
import { IDocumentTypesDto } from 'app/main/pages/documents/dto/document-listing.dto';
import { Data, RecipientRole, Validations } from 'app/main/pages/edit-template/edit-template.dto';
import { FieldBlockType } from 'app/main/pages/general-editor-tabs/content-tab/content-tab.dto';
import { FindAndReplaceService } from 'app/main/pages/general-editor-tabs/general-popover-settings/find-and-replace.service';
import { PopOver } from 'app/main/pages/general-editor-tabs/popover/popover';
import { FieldValidations, ICustomFieldDto } from 'app/main/pages/settings/documentfields/metadata-item.dto';
import { SharedStoreFacade } from 'app/main/shared/shared/store/shared-store.facade';
import { ICategory } from 'app/main/shared/shared/table/components/category-name/category-name.component';
import { UserTimeZoneFormatPipe } from 'app/pipes/user-timezone-format.pipe';
import { ADD_USER_SETTINGS } from 'app/store/actions/user.actions';
import { selectUser } from 'app/store/reducers/user_settings.reducer';
import * as roleSelectors from 'app/store/selectors/roles.selectors';
import { UserSettingsFacade } from 'app/store/user-settings.facade';
import flatpickr from 'flatpickr';
import { isEqual } from 'lodash';
import moment from 'moment';
import { ToastrService } from 'ngx-toastr';
import Personnummer from 'personnummer';
import { delay, distinctUntilChanged, filter, lastValueFrom, map, of, Subject, Subscription, take, takeUntil, tap } from 'rxjs';
import uuid from 'short-unique-id';
import SignaturePad from 'signature_pad';
import { generateCalculation } from './calculation';
import { ContentDraggableService } from './content-draggable/content-draggable.service';
import { defaultUserCompanySettings } from './default-user-settings';
import { DocumentFieldEmitterService } from './documentfield-emitter.service';
import { DomListenerService } from './dom-listener.service';
import { IExtraFiledFormInterface } from './extra-filed-form.interface';
import { applyRelations, resetDeletedRelation } from './fieldrelation';
import { clearButton } from './flatpickerButtonPlugin';
import { LocalStorageKeys } from './storage.service';
import { TipTapEditorService } from './tiptap-editor/tiptap-editor-service';
import { EditorInstance } from './tiptap-editor/tiptap-editor.dto';
import { UserSettings, Workspace } from './user-settings.dto';
import { FieldRenderService } from 'app/main/pages/edit-template/field-render.service';
import { ContentResizeService } from './content-resizable/content-resizable.service';
import { IDocumentDetailRootDTO } from 'app/main/pages/documents/dto/document-details.dto';
import { SigningStatusEnum } from 'app/main/signing-pages/enums/enum';
import { FieldInfo } from 'app/interfaces/field-base';
import { ValidationHandlerService } from './validation-handler/validation-handler.service';
import { FieldTypeEnum } from 'app/interfaces/fields.dto';
import { SelectedFieldEnum } from 'app/interfaces/fields.dto';
import { EditorEmitterService } from 'app/main/pages/events/editor-emitter.service';
import { SessionImageService } from './session-image.service';
import { SelectedRole } from 'app/main/pages/general-editor-tabs/general-popover-settings/general-popover-settings.dto';
import { DomHelperService } from './dom-helper.service';
import { DeviceDetectorService } from 'ngx-device-detector';



declare let $;
@Injectable({
  providedIn: 'root'
})
export class GlobalService implements OnDestroy {
  public basePath = 'https://storage.googleapis.com/simplesign-v3/';
  private renderer: Renderer2;



  constructor(
    public toastr: ToastrService,
    public store: Store,
    public _formBuilder: UntypedFormBuilder,
    private emitter: DocumentFieldEmitterService,
    private appService: AppService,
    rendererFactory: RendererFactory2,
    private _translocoService: TranslocoService,
    private _sharedStoreFacade$: SharedStoreFacade,
    private _addonPermissionService$: AddonPermissionService,
    private _userTimeZonePipe$: UserTimeZoneFormatPipe,
    private tipTapEditorService: TipTapEditorService,
    private userSettingsFacade$: UserSettingsFacade,
    private _sanitizer: DomSanitizer,
    private domListenerService: DomListenerService,
    private _fieldRenderService: FieldRenderService,
    private contentResizeService: ContentResizeService,
    private editorEmitterService: EditorEmitterService,
    private deviceService: DeviceDetectorService,
  ) {
    this.renderer = rendererFactory.createRenderer(null, null);
  }
  public uuid = new uuid({ length: 6, dictionary: 'number' });
  public user_settings: UserSettings;
  public name_format = 1; //1 = fullname, 0 = first and last name
  public timeOutSubscription: Subscription;
  private timeoutId?: ReturnType<typeof setTimeout>;
  public MIN_DISTANCE = 5; // minimum distance to "snap" to a guide
  public guides = []; // no guides available ... 
  public innerOffsetX // we'll use those during drag ... 
  public innerOffsetY // we'll use those during drag ... 
  public signatureObjects = {};
  public fonts = ['Montserrat', 'Delius', 'Leckerli One', 'Rochester', 'Stalemate', 'Clicker Script', 'Caveat', 'Inspiration', 'Gloria Hallelujah'];
  private validationsTitles = {
    numbers: 'Numbers',
    letters: 'Letters',
    numbers_letters: 'Numbers and Letters',
    email: 'Email',
    custom_regex_validation: 'Custom Validation',
    numbers_range: 'Number range',
    numbers_characters: 'Numbers of characters'
  };
  public roles = [];
  public fields_data = {};
  private destroy$ = new Subject<void>();
  public backspaceScriptOnEditor = null;
  // Max file size in bytes (20 MB)
  private MAX_FILE_SIZE = 20 * 1024 * 1024; // 20 MB in bytes
  public editorInstances: EditorInstance = {};
  public signingAreaRoleId;
  private readonly fieldMap: Record<SelectedFieldEnum, string> = {
    [SelectedFieldEnum.TextField]: "Content text field (Template/Document - section) - Text Field",
    [SelectedFieldEnum.CheckboxField]: "Content check Box field (Template/Document - section) - Check Box",
    [SelectedFieldEnum.DatepickerField]: "Content date picker field (Template/Document - section) - Date Picker",
    [SelectedFieldEnum.SignatureField]: "Content signing box field (Template/Document - section) - Signing Box",
    [SelectedFieldEnum.ListField]: "Content list field (Template/Document - section) - List",
    [SelectedFieldEnum.DroplistField]: "Content drop list (Template/Document - section) - Drop List"
  };
  /**
   * subscribe the store actions
   */
  async storeSubscribe() {
    this.store.pipe(select(roleSelectors.selectTemplateRole)).subscribe((state) => {
      this.roles = (state && Object.prototype.hasOwnProperty.call(state, 'roles')) ? state.roles : [];
    });

    this.store.pipe(select(selectUser)).subscribe((state) => {
      this.user_settings = (state && Object.prototype.hasOwnProperty.call(state, 'user_settings')) ? state.user_settings : {};
      this._addonPermissionService$.detectAddonPermission();
    });
  }

  allowedSelf() {
    return this;
  }

  /** 
   * /update the field property, tag,field
   * @param uuid 
   * @param object 
   */
  updateField(uuid: string | number, object: object, callback?: (fields: Field) => void, selfUpdate: boolean = false) {

    if (String(uuid) === 'blocks') {
      console.warn('Attempt to update reserved key "blocks" with updateField', object);
      return;
    }

    object['updated'] = 1;

    this.fields_data[uuid] = { ...this.fields_data[uuid], ...object };
    if (callback) {
      callback(this.fields_data[uuid]);
    }
    if (!selfUpdate && this.fields_data[uuid].selected_field != "5") {
      this.findAndUpdateSameField(this.fields_data[uuid]);
    }
    const field: Field = this.fields_data[uuid];

    if (field.tag_view == "1") {
      //TODO Fixes
      this.timeoutId = setTimeout(() => {
        const selector = document.querySelector(`.drag-fields[uuid="${field.uuid}"]`);
        if(selector){
          const selectedLabel = selector.querySelector('.selected-label').textContent;
          this.renderer.setAttribute(selector, 'data-label', selectedLabel);
        }
      }, 500);
    }
    this.editorEmitterService.setUnsavedChangesFlag(true);
    return this.fields_data[uuid];
  }



  /**
   * Find and update the field if have the same document field type
   *  
   * @param currentField 
   * @returns {void}
   */
  findAndUpdateSameField(currentField: Field): void {
    if (currentField.field_info === "custom_field") return;
    const findFields = this.findMatchingField(this.fields_data, 'selected_field_type_id', currentField.selected_field_type_id, currentField.field_section);
    const findAndReplaceService = AppInjector.get(FindAndReplaceService);
    findFields.forEach((field: Field) => {
      if (field.uuid != currentField.uuid) {
        this.fields_data[field.uuid] = { ...field, ...{ value: currentField.value, pre_checked: currentField.pre_checked, field_required: currentField.field_required, selected_for_role_id: currentField.selected_for_role_id } };
        findAndReplaceService.autoFillIfMatch(this.fields_data[field.uuid]);
      }
    });
  }


 

  /**
   * Marks a field as deleted by its UUID, updates related data, and removes it from the local fields data.
   *
   * @param uuid - The unique identifier of the field to delete. Can be a string or number.
   *
   * @remarks
   * - Sends a patch request to mark the field as deleted.
   * - Resets any deleted relations associated with the field.
   * - Unmarks the field as rendered.
   * - Removes the field from the `fields_data` object if it exists.
   */
  deleteField(uuid: string | number): void {
    const deletedField = this.fields_data[uuid];

    resetDeletedRelation(uuid, this, () => {
      this._fieldRenderService.unmarkRendered(String(uuid));
      delete this.fields_data[uuid];

      this.emitter.patchData({ uuid: String(uuid), is_deleted: 1 });
      this.editorEmitterService.setUnsavedChangesFlag(true);

      if (deletedField && typeof deletedField.order === 'number') {
        const deletedOrder = deletedField.order;
        Object.values(this.fields_data)
          .filter((field: Field) => this.getFieldTypeEnums.includes(field?.type as FieldTypeEnum) && field.order > deletedOrder)
          .forEach((field: Field) => {
            this.updateField(field.uuid, { order: field.order - 1 });
          });
      }
    });
  }


  /**
   * update the content block property;
   * @param uuid 
   * @param object 
   */
  updateBlock(uuid, object: object) {
    if (!this.fields_data['blocks'] || !this.fields_data['blocks'][uuid]) {
      return null;
    }
    this.fields_data['blocks'][uuid] = { ...this.fields_data['blocks'][uuid], ...object };
    this.editorEmitterService.setUnsavedChangesFlag(true);
    return this.fields_data['blocks'][uuid];
  }

  /**
   * delete the specific field
   * @param uuid
   */
  unsetBlockField(uuid, blockuuid) {
    if (Object.prototype.hasOwnProperty.call(this.fields_data, 'blocks')) {
      const blocks: Block = this.fields_data['blocks'][blockuuid];
      const fields = blocks.fields.filter(key => key != uuid);
      this.updateBlock(blockuuid, { fields: fields });
    }
  }

  deleteBlock(blockuuid: string | number) {
    if (Object.prototype.hasOwnProperty.call(this.fields_data, 'blocks')) {
      const block: Block = this.fields_data['blocks'][blockuuid];
      const fieldsToDelete = block && Array.isArray(block.fields) ? [...block.fields] : [];
      if (block && fieldsToDelete.length > 0) {
         for (const uuid of fieldsToDelete) {
          this.deleteField(uuid);
        }
      }
      this.editorEmitterService.setUnsavedChangesFlag(true);
      // Final guard: only delete if still present
      if (block) {
        delete this.fields_data['blocks'][blockuuid];
      }
    }
  }

  /**
   * Generate tag text behalf on uuid
   * @param uuid 
   * @returns Tag text or empty string
   */
  generateTagText(uuid: string | number) {
    const tagInterface = this.fields_data[uuid];
    if (tagInterface) {
      return `[${tagInterface['select_type_text']}]`
    }
    return '';
  }



  /**
   * reset field value
   * @param uuid 
   */
  resetField(uuid: string | number, hidden: number = 0) {
    const field: Field = this.fields_data[uuid];
    if(field && ![FieldInfo.ME, FieldInfo.RECIPIENT_FIELD].includes(field.field_info)){ 
      this.updateField(uuid, { value: '', hidden });
      const uuidSelector = document.querySelector('.drag-fields[uuid="' + uuid + '"]');
      if (field && field.type_of == 'tag') {
        if (uuidSelector?.querySelector('.selected-label')) {
          this.renderer.setProperty(uuidSelector?.querySelector('.selected-label'), 'textContent', this.generateTagText(uuid));
        }
        if (uuidSelector?.querySelector('input,textarea')) {
          this.renderer.setAttribute(uuidSelector?.querySelector('input,textarea'), 'value', '');
        }
      } else {
        if (field.type == 'checkbox') {
          this.renderer.removeClass(uuidSelector, 'checkbox-prechecked');
        } else if (field.type == 'droplist') {
          this.renderer.setProperty(uuidSelector?.querySelector('.selected-option-label'), 'textContent', '');
        } else if (field.type == 'list') {
          uuidSelector?.querySelectorAll('.multiChoice-item').forEach((ele: HTMLElement) => {
            this.renderer.removeClass(ele, 'multiChoice-active');
          });
        }
        const inputSelector = uuidSelector?.querySelector('.datepicker-field');
        if (inputSelector && inputSelector['_flatpickr']) {
          inputSelector['_flatpickr'].clear();
        }
        if (uuidSelector?.querySelector('input,textarea')) {
          this.renderer.setAttribute(uuidSelector?.querySelector('input,textarea'), 'value', '');
          this.renderer.setProperty(uuidSelector?.querySelector('input,textarea'), 'textContent', '');
          //alternet work if render fails to update
          (uuidSelector?.querySelector('input,textarea') as HTMLInputElement).value = '';
          (uuidSelector?.querySelector('input,textarea') as HTMLInputElement).textContent = '';
        }
      }
    }else {
      this.updateField(uuid, { hidden });
    }
  }

  /**
   * initialize draggable on fields for editor 
   * @param field 
   * @param element 
   */
  initializeDragOnEditorFields(field, element) {
    if(!field) return;
    const elementObj = { parent: String, top: Number, left: Number };
    element.draggable({
      containment: element.parent().find('.pdf-block-editor-box'),
      scroll: false,
      cancel: '.multiChoice-item-container, .droplist-dropdown, .form-builder-table, .guide, input, textarea',

      start() {
        $(this).data('preventBehaviour', true);
        document.querySelectorAll('.fieldsPopover').forEach((ele) => {
          ele.remove();
        });
      },

      drag() {
        $(this).css('pointer-events', 'none')
      },

      stop() {
        $(this).css('pointer-events', '');
        if ($(this).attr('pdfblock') == 1) {
          $('div.content-box').removeClass('add-pdf-block');
        } else if (!$(this).parent().hasClass('draging-started')) {
          $(this).css('top', elementObj.top)
          $(this).css('left', elementObj.left)
        }
        if (this.timeOutSubscription) this.timeOutSubscription.unsubscribe();
        this.timeOutSubscription = of(null)
          .pipe(
            delay(5),
            take(1)
          ).subscribe(() => {
            $('.contentBox-popover-container').remove()
          });
        $("#guide-v, #guide-h, #guide-hb, #guide-vr").hide();
      },
    })

    if (typeof $(element).resizable('instance') != 'undefined') $(element).resizable('destroy');
    if (field == 'datepicker-field' || field == 'text-field' || field == 'droplist-field' || field == 'multichoice-field') {
      $(element).resizable({
        handles: 'e, s',
        resize() { },
      })
    } else if (field == 'signature-field') {
      $(element).resizable({
        handles: 'se',
        minWidth: 115,
        minHeight: 50,
      })
    } else if (field == 'image-field') {
      $(element).resizable({
        handles: 'se',
        minWidth: 230,
        minHeight: 160,
      })
    }

  }

  /**
   * show the indicator on editor when drag the field
   */
  InitializeCheckDropElement() {
    const that = this.allowedSelf();
    this.domListenerService.addListener('.content-box-inner', 'mousemove', function (event) {
      that.renderer.addClass(event.target, 'draging-started');
    });
    this.domListenerService.addListener('.content-box-inner', 'mouseout', function (event) {
      that.renderer.removeClass(event.target, 'draging-started');
    });
  }

  /**
   * initiazlize hover effect when drags the elements
   * @param params 
   */
  initializeHoverOnDragStart(params: { block: string }) {
    const that = this.allowedSelf();
    this.domListenerService.addListener(document.querySelectorAll('div.content-box'), 'mouseenter', function (event) {
      const target = event.target as HTMLElement;
      that.removeClassFromElements('.content-box-enter', 'content-box-enter');
      let indicator = '';
      if ((params.block === FieldBlockType.ImageBlock || params.block === FieldBlockType.ContentBox || params.block === FieldBlockType.ContentFields) &&
        !target.classList.contains('form-builder-block') &&
        !target.classList.contains('attachment-block') &&
        !target.classList.contains('pdf-block') &&
        !target.classList.contains('upload-pdf-block') &&
        !target.classList.contains('page-break-block') &&
        !target.classList.contains('product-table-block')) {
        indicator = `<span position='right' class='dropIndicator-right'></span>
                         <span class='dropIndicator-left' position='left'></span>`;
      }
      const index = that.getIndexAmongSiblings(target, '.content-box');
      if (index === 0 && params.block !== FieldBlockType.PageBreak) {
        indicator += `<span class='dropIndicator-top' position='top'></span>`;
      }
      target.insertAdjacentHTML('beforeend', `<span class='dropIndicator-bottom' position='bottom'></span>${indicator}`);
      that.initializeZoneIndicator();
      that.renderer.addClass(target, 'content-box-enter');
    });
    this.domListenerService.addListener(document.querySelectorAll('div.content-box'), 'mouseleave', function (event) {
      const target = event.target as HTMLElement;
      that.removeElements(target, '.dropIndicator-bottom');
      that.removeElements(target, '.dropIndicator-top');
      that.removeElements(target, '.dropIndicator-right');
      that.removeElements(target, '.dropIndicator-left');
      that.removeClassFromElements('.content-box-enter', 'content-box-enter');
    });
    // Mouseenter event for .template-page
    this.domListenerService.addListener('div.template-page', 'mouseenter', function (event) {
      that.renderer.addClass(event.target, 'addContentBox');
    });
    this.domListenerService.addListener('div.template-page', 'mouseleave', function (event) {
      that.renderer.removeClass(event.target, 'addContentBox');
    });

    this.domListenerService.addListener(document.querySelectorAll('.content-box-inner'), 'mouseenter', function (event) {
      const target = event.target as HTMLElement;
      if (![FieldBlockType.ImageBlock, FieldBlockType.ContentBox, FieldBlockType.ContentFields].includes(params.block as FieldBlockType)) {
        return;
      }
      that.removeClassFromElements('.content-box-inner', 'content-box-inner-hover');
      const parentBox = target.closest('.content-box') as HTMLElement;
      if (!parentBox) return;
      that.renderer.addClass(parentBox, 'content-box-enter');
      // Measure space for parent content-box
      const rect = parentBox.getBoundingClientRect();
      const spaceBelow = rect.height;

      const enoughSpace = spaceBelow > 60;
      if (!enoughSpace || parentBox.querySelectorAll('.content-box-inner').length === 1) {
        // Don't show anything
        return;
      }
      // Insert indicators based on mouse position
      const indicator = `
        <span class="dropIndicator-inner-bottom" position="bottom"></span>
      `;

      target.insertAdjacentHTML('beforeend', indicator);
      that.initializeZoneIndicator();
      that.renderer.addClass(target, 'content-box-inner-hover');
      if (parentBox) {
        const bottomIndicator = parentBox.querySelectorAll('.dropIndicator-right, .dropIndicator-left, .dropIndicator-bottom, .dropIndicator-top');
        if (bottomIndicator) {
          bottomIndicator.forEach((el: HTMLElement) => {
            el.style.display = 'none';
          });
        }
      }
    });

    this.domListenerService.addListener(document.querySelectorAll('.content-box-inner'), 'mouseleave', function (event) {
        const target = event.target as HTMLElement;
        that.removeElements(target, '.dropIndicator-inner-bottom');
        that.removeClassFromElements('.content-box-inner-hover', 'content-box-inner-hover');
        const parentBox = target.closest('.content-box') as HTMLElement;
        if (parentBox) {
          const bottomIndicator = parentBox.querySelectorAll('.dropIndicator-right, .dropIndicator-left, .dropIndicator-bottom, .dropIndicator-top');
          if (bottomIndicator) {
            bottomIndicator.forEach((el: HTMLElement) => {
              el.style.display = '';
            });
          }
        }
    });
  }

  removeElements(parent: HTMLElement, selector: string) {
    parent.querySelectorAll(selector).forEach(el => el.remove());
  }

  removeClassFromElements(selector: string, className: string) {
    document.querySelectorAll(selector).forEach(el => this.renderer.removeClass(el, className));
  }

  getIndexAmongSiblings(element: HTMLElement, selector: string): number {
    const siblings = Array.from(element.parentNode?.querySelectorAll(selector) || []);
    return siblings.indexOf(element);
  }

  /**
   * init the zone indicator on container corner here user can drop the elemenet
   */
  initializeZoneIndicator() {
    this.domListenerService.addListener(document.querySelectorAll('.dropIndicator-right, .dropIndicator-left, .dropIndicator-bottom, .dropIndicator-top, .dropIndicator-inner-bottom'), 'mousemove', (event) => {
      const element = event.target as HTMLInputElement;
      const position = element.getAttribute('position');
      const templatePage = document.querySelectorAll('.template-page');
      templatePage.forEach((page: HTMLElement) => {
        if (page) {
          this.renderer.setAttribute(page, 'position', position);
        }
      })
    });
  }




  getUserDefaultWorkSpace() {
    return this.userSettingsFacade$._userSettings$.pipe(
      filter(x => !!x),
      distinctUntilChanged((prev, curr) => !isEqual(prev, curr)), // Take only one emission
      map(user_settings => user_settings?.workspaces.find(x => x?.default === true))
    );
  }




  getUserCompanySettings() {
    return this.getUserDefaultWorkSpace().pipe(
      takeUntil(this.destroy$),
      map((x: Workspace) => x?.company ?? defaultUserCompanySettings)
    );
  }




  /**
   * sort the content box
   */
  contentBoxSort() {
    const contentDraggableService = AppInjector.get(ContentDraggableService);
    contentDraggableService.enable();
    this.contentResizeService.initResizing('.content-box');
  }

  /**
   * append the highlights when sort
   */
  appendHighlights() {
    const highlightElement = document.querySelector('.ui-state-highlight');
    // Create the outer span with class "highlightSpanOne"
    const outerSpan = this.renderer.createElement('span');
    this.renderer.addClass(outerSpan, 'highlightSpanOne');

    // Create the inner span with class "highlightSpanThree"
    const innerSpan = this.renderer.createElement('span');
    this.renderer.addClass(innerSpan, 'highlightSpanThree');

    // Append the inner span to the outer span
    this.renderer.appendChild(outerSpan, innerSpan);

    // Append the outer span to the parent element
    this.renderer.appendChild(highlightElement, outerSpan);
  }

  /**
   * init the tiptap editor for template
   * @param params 
   */
  initEditor(params: {uuid: string|number, clickCoords?: {left: number, top: number}}) {
    if (document.querySelectorAll(`.editor${params.uuid}`).length) {
      document.querySelectorAll(`.editor${params.uuid}`).forEach((editor: HTMLElement) => {
        this.tipTapEditorService.initEditor(editor, params.uuid, params.clickCoords);
      });
    }
  }

  destroyEditor(editorId: number) {
    this.tipTapEditorService.destroy(editorId, this);
  }

  /**
   * show the success toaster
   * @param message 
   */
  toastrSuccess(message, config = {}) {
    this.toastr.toastrConfig.preventDuplicates = true;
    this.toastr.success(message, this._translocoService.translate("Toaster success title (Toaster title) - Success"),
      {
        toastClass: 'toast ngx-toastr',
        closeButton: true,
        ...config
      });
  }



  /**
   * array to form data for specific conditions
   * @param formValue 
   */
  toFormData<T>(formValue: T) {
    const formData = new FormData();
    for (const key of Object.keys(formValue)) {
      const value = formValue[key];
      formData.append(key, value);
      // if (value != null && value != '') {
      // }
    }
    return formData;
  }



  /**
   * Remove null values from object
   * @param formData 
   * @returns Object
   */
  removeEmptyKeysFromObject(formData) {
    Object.keys(formData).forEach(key => formData[key] === null && delete formData[key]);
    return formData;
  }




  /**
   * show error toaster
   * @param message 
   */
  toastrError(message) {
    this.toastr.toastrConfig.preventDuplicates = true;
    this.toastr.error(message, this._translocoService.translate("Toaster error title (Toaster title) - Error"),
      {
        toastClass: 'toast ngx-toastr',
        closeButton: true,
        positionClass: 'toast-bottom-left',
      });
  }




  /**
   * Determines whether a specific field should be disabled based on its locked status
   * or the signing status of the template.
   *
   * @param template - The document template object containing status information.
   * @param field - The field to check for the locked status.
   * @returns `true` if the field is locked or the template is signed; otherwise, `false`.
   */
  shouldFieldDisabled(template: IDocumentDetailRootDTO, field: Field): boolean {
    return field?.is_locked === 1 || template?.template?.status === SigningStatusEnum.SIGNED || template?.template.can_edit === false;
  }



  /**
   * show wanring toaster
   * @param message 
   */
  toastrWarning(message) {
    this.toastr.toastrConfig.preventDuplicates = true;
    this.toastr.warning(message, this._translocoService.translate("Toaster warning title (Toaster title) - Warning"),
      {
        toastClass: 'toast ngx-toastr',
        closeButton: true
      });
  }

  /**
   * converts a flatpickr dateFormat token string (e.g. 'Y-m-d', 'Y M d') into the equivalent moment.js format
   * @param dateFormat
   */
  flatpickrToMomentFormat(dateFormat: string): string {
    const tokenMap: Record<string, string> = { Y: 'YYYY', m: 'MM', M: 'MMM', d: 'DD' };
    return dateFormat.replace(/[YmMd]/g, (token) => tokenMap[token]);
  }

  /**
   * refreshes each `.datepicker-field` input's displayed value from its canonical stored value (fields_data[uuid].value)
   * so it matches the currently active date format before flatpickr parses it
   * @param element
   * @param momentFormat
   */
  syncDatepickerInputValues(element, momentFormat: string): void {
    const that = this.allowedSelf();
    const inputs: HTMLInputElement[] = element instanceof HTMLElement ? [element as HTMLInputElement] : Array.from(element as ArrayLike<HTMLInputElement>);
    inputs.forEach((input) => {
      if (!input?.classList?.contains('datepicker-field')) return;
      const uuid = input.closest('.drag-fields')?.getAttribute('uuid');
      const field = uuid ? (that.fields_data as Record<string, Field>)[uuid] : undefined;
      if (field?.value && moment(field.value, 'YYYY-MM-DD', true).isValid()) {
        const formattedValue = moment(field.value, 'YYYY-MM-DD').format(momentFormat);
        that.renderer.setAttribute(input, 'value', formattedValue);
        input.value = formattedValue;
      }
    });
  }

  /**
   * init the flatpicker = datepicker
   * @param element
   * @param open
   */
  async initializeFlatpicker(element, open = false) {
    const that = this.allowedSelf();
    const dateFormat = await lastValueFrom(this._userTimeZonePipe$.transform(false));
    if (element) {
      const momentFormat = that.flatpickrToMomentFormat(dateFormat);
      that.syncDatepickerInputValues(element, momentFormat);
      flatpickr((element), {
        disableMobile: true,
        dateFormat: dateFormat,
        plugins: [clearButton({
          onClick: (index, fp) => {
            fp.clear();
            fp.close();
            const fpElement = (fp.input as HTMLInputElement);
            const uuid = fpElement.closest('.drag-fields').getAttribute('uuid');
            that.updateField(uuid, { value: '', alt_value: '' });
            that.emitter.patchData({ uuid: uuid, value: '', refresh: 1 });
            this.renderer.setAttribute(fpElement, 'value', '');
            applyRelations(uuid, that);
          }
        })],
        onValueUpdate: function (selectedDates, dateStr, instance) {
          const uuid = $(instance.input).parents('.drag-fields').attr('uuid');
          const date = moment(dateStr, momentFormat).format('YYYY-MM-DD');
          that.updateField(uuid, { value: date, alt_value: dateStr });
          $(instance.input).attr('value', dateStr);
          if ($(instance.input).hasClass('tag-date')) {
            $(instance.input).text(dateStr);
            that.emitter.patchData({ uuid: uuid, value: date, refresh: 1 });
            applyRelations(uuid, that);
          }
          else if ($(instance.input).hasClass('datepicker-field')) {
            that.emitter.patchData({ uuid: uuid, value: date, refresh: 1 });
            applyRelations(uuid, that);
          }
          const validationHandlerService = AppInjector.get(ValidationHandlerService);
          validationHandlerService.resetFieldHighlight(uuid);
        },

        onReady: function (selectedDates, dateStr, instance) {
          if ($(instance.input).hasClass('tag-date')) {
            const isDateValid = !isNaN(new Date(instance.input.textContent).valueOf());
            instance.setDate((isDateValid ? instance.input.textContent : null))
            if (open) {
              instance.open();
            }
          } else if (instance.input.closest('.content-draggable-field')) {
            if (open) {
              instance.open();
            }
          }
        },
        onClose: function (selectedDates, dateStr, instance) {
          const uuid = $(instance.input).parents('.drag-fields').attr('uuid');
          if ($(instance.input).hasClass('tag-date')) {
            instance.destroy();
          } else if ($(instance.input).hasClass('datepicker-field')) {
            if (instance.input.closest('.content-draggable-field')) {
              instance.destroy();
              that.renderer.setAttribute(instance.input, 'readonly', 'true');
            }
            if (dateStr == "") {
              that.emitter.patchData({ uuid: uuid, value: dateStr, refresh: 1 });
              applyRelations(uuid, that);
            }
          }
        }
      });
    }
  }




  updateDatePickerTagFormat(uuid: string, value: string) {
    try {
      const selector = document.querySelector('.drag-fields[uuid="' + uuid + '"]');
      const tagView = selector.querySelector('span.tag-date');
      this.renderer.setProperty(tagView, 'textContent', value);
    } catch (error) { /* empty */ }
  }




  /**
   * destroy the flatpicker = datepicker
   * @param element 
   */
  destroyFlatpicker(element) {
    flatpickr(element).destroy();
  }

  /**
   * init the signature using 3rd tools SignaturePad
   * @param element 
   * @param uuid 
   * @param reAssigned 
   * @param penColor 
   */
  initializeSignature(element, uuid, reAssigned = [], penColor = '#0061ef') {
    const that = this.allowedSelf();
    if (element) {
      that.signatureObjects[uuid] = new SignaturePad(element, {
        minDistance: 0,
        minWidth: 0.5,
        maxWidth: 3,
        penColor: penColor,
        backgroundColor: 'rgba(0,0,0,0)',
      });

      that.signatureObjects[uuid].addEventListener("beginStroke", (e) => {
        $(e.detail.target.parentElement).find('span.field-label').css('display', 'none');
      });
      //setTimeout(() => {
      const ratio = Math.max(window.devicePixelRatio || 1, 1);
      element.width = element.offsetWidth * ratio;
      element.height = element.offsetHeight * ratio;
      element.getContext("2d").scale(ratio, ratio);
      if (that.signatureObjects[uuid].isEmpty()) {
        that.signatureObjects[uuid].clear(); // otherwise isEmpty() might return incorrect value
      }
      if (reAssigned) {
        that.signatureObjects[uuid].fromData(reAssigned, { clear: false });
      }
      //}, 600);
    }
  }

  /**
   * remove the signature
   * @param uuid 
   */
  removeSignature(uuid) {
    if (Object.prototype.hasOwnProperty.call(this.signatureObjects, uuid)) {
      delete this.signatureObjects[uuid];
    }
  }

  /**
   * resize the signature when resize the box
   * @param element 
   * @param uuid 
   * @param data 
   */
  resizeSignatureBox(element, uuid, data) {
    if (element) {
      const ratio = Math.max(window.devicePixelRatio || 1, 1);
      element.width = element.offsetWidth * ratio;
      element.height = element.offsetHeight * ratio;
      element.getContext("2d").scale(ratio, ratio);
      if (this.signatureObjects[uuid].isEmpty()) {
        this.signatureObjects[uuid].clear(); // otherwise isEmpty() might return incorrect value
      }
      if (data) {
        this.signatureObjects[uuid].fromData(data, { clear: false });
      }
    }
  }



  /**
   * Here we are checking if the selected workspace
   * @return array | undefined
   */
  extractWorkSpaceOrganization() {
    const {
      id, name, email, phone, organization, registration_number, address, zipcode, city,
      billingcountry, vatnumber, industry, tenant_owner
    } = this.userData();

    const workSpace = this.getDefaultWorkSpace();
    if (workSpace?.is_organization) {
      return { ...workSpace, name, email, phone, tenant_owner };
    }

    return {
      id, name, email, phone, organization, registration_number, address, zipcode, city,
      billingcountry, vatnumber, industry, tenant_owner
    };
  }




  /**
   * init the resizable on fields
   * @param element 
   * @param containment 
   */
  initializeContentImageResize(element) {
    $(element).resizable({
      // containment:containment,
      minWidth: 60,
      handles: 'ne, se, sw, nw',
    })
  }

  /**
   * adjust the content box width when drop more boxes beside of content box
   */
  adjustInnerContentBoxWidthOnAdd(all = false, elementOrSelector: HTMLElement | string = '.content-box-enter') {
    if (all) {
      document.querySelectorAll('.content-box').forEach((contentBox: HTMLElement) => {
        this.setWidth(contentBox);
      })
    } else if (typeof elementOrSelector === 'string') {
      const contentBox = document.querySelector(elementOrSelector) as HTMLElement;
      this.setWidth(contentBox);
    }else{
      this.setWidth(elementOrSelector);
    }
    this.contentResizeService.initResizing('.content-box');
  }

  setWidth(contentBox: HTMLElement, initResize: boolean = false): void {
    if (!contentBox) return;

    // Get direct children of `.content-box-row` that are visible
    const contentBoxRow = contentBox.querySelector('.content-box-row');
    if (!contentBoxRow) return;

    const rowItems = Array.from(contentBoxRow.children)
      .filter((el): el is HTMLElement => el instanceof HTMLElement && el.offsetParent !== null);

    const resized = rowItems.filter(el => el.getAttribute('data-resized') === 'true');
    const notResized = rowItems.filter(el => el.getAttribute('data-resized') !== 'true');

    const totalResizedWidth = resized.reduce((sum, el) => {
      return sum + parseFloat(el.style.width || '0');
    }, 0);

    const totalBoxes = rowItems.length;

    if (notResized.length === 0 && resized.length > 0) {
      // Normalize resized widths to 100%
      resized.forEach(el => {
        const original = parseFloat(el.style.width || '0');
        const newWidth = (original / totalResizedWidth) * 100;
        this.renderer.setStyle(el, 'width', `${newWidth}%`);
      });
    }

    else if (notResized.length > 0 && totalResizedWidth >= 100) {
      const remainingWidth = 100 / totalBoxes;

      resized.forEach(el => {
        const currentWidth = parseFloat(el.style.width || '0');
        const scaledWidth = (currentWidth / totalResizedWidth) * (100 - remainingWidth * notResized.length);
        this.renderer.setStyle(el, 'width', `${scaledWidth}%`);
      });

      notResized.forEach(el => {
        this.renderer.setStyle(el, 'width', `${remainingWidth}%`);
      });
    }

    else if (notResized.length > 0) {
      const remainingWidth = 100 - totalResizedWidth;
      const widthForEach = remainingWidth / notResized.length;

      notResized.forEach(el => {
        this.renderer.setStyle(el, 'width', `${widthForEach}%`);
      });
    }

    if (initResize) {
      this.contentResizeService.initResizing('.content-box');
    }
  }

  /**
   * init the col resizer for the table columns
   * @param element 
   */
  initializColumnResizer(element) {
    $(element).prev('.JCLRgrips').remove();
    $(element).colResizable({ liveDrag: true, partialRefresh: true });
  }

  /**
   * generate the random color
   */
  generateRandomColor() {
    const letters = '0123456789ABCDEF';
    let color = '#';
    for (let i = 0; i < 6; i++) {
      color += letters[Math.floor(Math.random() * 16)];
    }
    return color;
  }

  /**
   * get the initials from string
   * @param initial 
   */
  getInitials(initial) {
    const allNames = initial.trim().split(' ');
    const initials = allNames.reduce((acc, curr, index) => {
      if (index === 0 || index === allNames.length - 1) {
        acc = `${acc}${curr.charAt(0).toUpperCase()}`;
      }
      return acc;
    }, '');
    return initials;
  }




  /**
   * Get user data
   * @returns userdata
   */
  userData() {
    const userData = localStorage[LocalStorageKeys.USER];
    if (userData != undefined && userData != null && userData != '') {
      return JSON.parse(userData);
    }
  }



  /**
   * Update user settings
   * @async
   */
  updateUsersettings() {
    this.appService.settings().subscribe({
      next: (user_settings: UserSettings) => {
        this.updateUserSettingInState(user_settings);
        this._addonPermissionService$.detectAddonPermission();
      }
    });
  }



  updateUserSettingInState(user_settings: UserSettings) {
    this.store.dispatch(new ADD_USER_SETTINGS(user_settings));
    this._sharedStoreFacade$.saveUserTimezone(user_settings?.user_timezone);
  }



  /**
   * Create dynamic decimal validator
   * @param min 
   * @param max 
   * @param decimalPlaces 
   * @returns ERROR || NULL
   */
  createDynamicDecimalValidator(min: number, max: number, decimalPlaces: number) {
    if (min >= max || decimalPlaces < 0) {
      throw new Error('Invalid arguments for dynamic decimal validator');
    }

    return (control: AbstractControl): { [key: string]: string | number | boolean } | null => {

      const value = parseInt(control.value);
      if (control.value == '') {
        return { invalidNumber: true };
      }

      if (isNaN(value)) {
        return { invalidNumber: true };
      }

      if (value < min || value > max) {
        return { outOfRange: true };
      }

      const decimals = control.value.toString().indexOf('.') != -1 ? control.value.split('.') : null;
      if (decimals != null && decimals[1].length > decimalPlaces) {
        return { invalidDecimalPlaces: true };
      }

      return null;
    };
  }

  /**
   * Create person number validator
   * @returns ERROR || NULL
   */
  createpPersonnummerValidator() {
    return (control: AbstractControl): { [key: string]: string | boolean | number } | null => {
      if (!Personnummer.valid(control.value)) {
        return { invalidPersonNummer: true };
      }
    }
  }


  /**
   * Create person number validator
   * @returns ERROR || NULL
   */
  createpPhoneNoValidator() {
    return (control: AbstractControl): { [key: string]: string | number | boolean } | null => {
      if (control.value != '') {
        if (!/^[\\+]?[(]?[0-9]{3}[)]?[-\s\\.]?[0-9]{3}[-\s\\.]?[0-9]{4,6}$/im.test(control.value)) {
          return { invalidPhoneNo: true };
        }
      }
    }
  }


  /**
   * Return validation form as a form group
   * @param data 
   * @returns 
   */
  validationFormGroup(data: FieldValidations) {
    return this._formBuilder.group({
      id: [data.id ?? 0],
      fieldable_id: [data.fieldable_id ?? 0],
      type: [data.type ?? null],
      validation_type: [data.validation_type ?? null],
      number_validation_type: [data.number_validation_type ?? null],
      min: [data.min ?? null],
      max: [data.max ?? null],
      decimals: [data.decimals ?? null],
      regular_expression: [data.regular_expression ?? null],
      validation_help_text: [data.validation_help_text ?? null],
    });
  }


  /**
   * Get validation title like Email, Number of characters
   * @param validations 
   * @returns 
   */
  getValidationTitle(validations: Validations) {
    if (validations == null) return null;
    if (validations.validation_type == 'numbers') {
      if (validations.number_validation_type != null) {
        return this.validationsTitles[validations.number_validation_type];
      }
      return this.validationsTitles[validations.validation_type]
    }
    return this.validationsTitles[validations.validation_type];
  }





  /**
   * Apply validations
   * @param field 
   * @returns 
   */
  setValidationsOnFields(field) {
    const validations = field?.validations;
    let validator = null;
    if (validations?.validation_type == 'numbers') {
      validator = [Validators.pattern(/^[-,0-9 ]+$/)];
      if (validations?.number_validation_type == 'numbers_characters') {
        validator = [Validators.minLength(validations?.min), Validators.maxLength(validations?.max), Validators.pattern(/^[-,0-9 ]+$/)]
      }
      else if (validations?.number_validation_type == 'numbers_range') {
        validator = [this.createDynamicDecimalValidator(validations?.min, validations?.max, validations?.decimals), Validators.pattern(/^[-,0-9 .]+$/)]
      }
    }
    else if (validations?.validation_type == 'letters') {
      validator = [Validators.maxLength(validations?.max), Validators.pattern(/^[A-Za-zÀ-ÖØ-öø-ÿĄąĆćĘęŁłŃńÓóŚśŹźŻż -._]+$/)];
    }
    else if (validations?.validation_type == 'numbers_letters') {
      validator = [Validators.maxLength(validations?.max), Validators.pattern(/^[A-Za-zÀ-ÖØ-öø-ÿĄąĆćĘęŁłŃńÓóŚśŹźŻż0-9 -._]+$/)];
    }
    else if (validations?.validation_type == 'email') {
      validator = [Validators.pattern(/^[\w-\\.]+@([\w-]+\.)+[\w-]{2,4}$/)];
    }
    else if (validations?.validation_type == 'custom_regex_validation') {
      let pattern = validations?.regular_expression.toString();
      if (pattern[0] == '/') {
        pattern = pattern.slice(1);
      }
      if (pattern[pattern.length - 1] == '/') {
        pattern = pattern.slice(0, -1);
      }
      validator = [Validators.pattern(pattern)];
    }
    return validator;
  }


  /**
 * Apply validations
 * @param field 
 * @returns 
 */
  setValidationsOnRequiredFields(field: IExtraFiledFormInterface) {
    let validator = null;
    if (field.is_enable == 0) {
      return validator;
    }
    //ignore the validation for the last name
    const requiredValidation = (field.required && field.key !== "last_name") ? true : false;
    const validations = field?.validations;
    if (validations?.validation_type == 'numbers') {
      validator = [this.conditionalRequiredValidator(requiredValidation), Validators.pattern(/^[-,0-9 ]+$/)];
      if (validations?.number_validation_type == 'numbers_characters') {
        validator = [this.conditionalRequiredValidator(requiredValidation), Validators.minLength(validations?.min), Validators.maxLength(validations?.max), Validators.pattern(/^[-,0-9 ]+$/)]
      }
      else if (validations?.number_validation_type == 'numbers_range') {
        validator = [this.createDynamicDecimalValidator(validations?.min, validations?.max, validations?.decimals), Validators.pattern(/^[-,0-9 .]+$/)]
      }
    }
    else if (validations?.validation_type == 'letters') {
      validator = [this.conditionalRequiredValidator(requiredValidation), Validators.maxLength(validations?.max), Validators.pattern(/^[A-Za-zÀ-ÖØ-öø-ÿĄąĆćĘęŁłŃńÓóŚśŹźŻż -._]+$/)];
    }
    else if (validations?.validation_type == 'numbers_letters') {
      validator = [this.conditionalRequiredValidator(requiredValidation), Validators.maxLength(validations?.max), Validators.pattern(/^[A-Za-zÀ-ÖØ-öø-ÿĄąĆćĘęŁłŃńÓóŚśŹźŻż0-9 -._]+$/)];
    }
    else if (validations?.validation_type == 'email') {
      validator = [this.conditionalRequiredValidator(requiredValidation), Validators.pattern(/^[\w-\\.]+@([\w-]+\.)+[\w-]{2,4}$/)];
    }
    else if (validations?.validation_type == 'custom_regex_validation') {
      let pattern = validations?.regular_expression.toString();
      if (pattern[0] == '/') {
        pattern = pattern.slice(1);
      }
      if (pattern[pattern.length - 1] == '/') {
        pattern = pattern.slice(0, -1);
      }
      validator = [this.conditionalRequiredValidator(requiredValidation), Validators.pattern(pattern)];
    } else if (field.name == 'Personal ID number' && field.validation == 1) {
      validator = [this.conditionalRequiredValidator(requiredValidation), this.createpPersonnummerValidator()];
    } else if (field.name == 'Phone no') {
      validator = [this.conditionalRequiredValidator(requiredValidation)];
    } else if (field.name == 'Email') {
      validator = [this.conditionalRequiredValidator(requiredValidation), Validators.pattern(/^[\w-\\.]+@([\w-]+\.)+[\w-]{2,4}$/)];
    } else {
      validator = [this.conditionalRequiredValidator(requiredValidation)];
    }
    return validator;
  }


  /**
   * check the validation for fields if field is required
   * @param isRequired 
   */
  conditionalRequiredValidator(isRequired: boolean): ValidatorFn {
    return (control: AbstractControl): { [key: string]: null } | null => {
      if (isRequired) {
        return Validators.required(control);
      } else {
        return null;
      }
    };
  }

  /**
   * pattern for characters
   * @param marker 
   * @param minChars 
   */
  createPattern(marker, minChars) {
    // Match also diacritic characters (#2491).
    let pattern = '\\' + marker + '[_a-zA-Z0-9À-ž]';

    if (minChars) {
      pattern += '{' + minChars + ',}';
    } else {
      pattern += '*';
    }

    pattern += '$';

    return new RegExp(pattern);
  }

  /**
   * backpsace script for remove or update fields;
   * @param event 
   */
  backSpaceScript() {
    for (const key in this.fields_data) {
      if (Object.prototype.hasOwnProperty.call(this.fields_data, key)) {
        const field: Field = this.fields_data[key];
        const uuidSelector = document.querySelector('.drag-fields[uuid="' + field?.uuid + '"]');
        if (!uuidSelector) {
          if (field?.type === 'text' && field?.uuid) {
            generateCalculation(field?.uuid, this);
          }
          if (!field?.semi_hidden_field && !field?.pinned_field && field?.uuid) {
            this.deleteField(field?.uuid);
          }
        }
      }
    }
  }


  /**
   * Get default workspace id
   * @returns workspaceID 
   */
  getDefaultWorkSpaceId() {
    const defaultWordSpace = this.getDefaultWorkSpace();
    return defaultWordSpace ? defaultWordSpace.id : null;
  }




  getDefaultWorkSpace() {
    return this.userData()?.workspaces?.find((item: Workspace) => item.default == true);
  }

  /**
   * Update user data
   * @param userData 
   */
  updateUserData(userData: UserSettings) {
    localStorage.setItem(LocalStorageKeys.USER, JSON.stringify(userData));
    const updatedUserSettings = { ...this.user_settings, ...userData };
    this.store.dispatch(new ADD_USER_SETTINGS(updatedUserSettings));
  }

  /**
   * get the section name by route
   * @param route 
   */
  getTheSectionByRoute(route: string) {
    let section = '';
    switch (route) {
      case 'edit-template':
        section = 'Template'
        break;
      case 'send-document':
        section = 'Document'
        break;
      case 'content-library':
        section = 'ContentLibrary'
        break;
    }
    return section;
  }

  getRoutePath(type: string): string {
    const routeMappings: { [key: string]: string } = {
      contentlibrary: 'content-libraries',
      template: 'templates',
      templates: 'templates',
      document: 'documents',
    };
    return routeMappings[type] || '';
  }

  /**
   * default editor hardcode when create new template/document
   */
  defaultEditor(params: { uuid: number }) {
    const popOver = AppInjector.get(PopOver);
    const uuid = params?.uuid ?? this.uuid();
    const editorinstance = document.createElement('div');
    editorinstance.className = 'content-box content-box-block content-editor-block marginminus70'
    editorinstance.innerHTML = `<div class="sort-helper"></div><div class="content-box-row"><div class="rowItem">${popOver.listingPopover({ classType: 'content-box', uuid: uuid })}<div class="content-box-inner" editor-uuid="${uuid}" type="content box"><div class="editor${uuid} cke-editor-box editor-word-wrap" editor-uuid="${uuid}" contenteditable="false" cke-placeholder="Type / for quick actions"><div></div></div></div></div></div>`;
    return editorinstance.outerHTML.replace(/\\t|\\n|\\(?=")/g, '');
  }

  /**
   * get the list multi chooice value
   * @param uuid 
   */
  getOptionValues(uuid: string) {
    const values = [];
    const optionsIds = [];
    document.querySelectorAll('[uuid="' + uuid + '"] .multiChoice-active').forEach((element: HTMLElement) => {
      const metadataId = element.getAttribute('option_uuid');
      const multiChoice = this.findDroplistById(uuid, metadataId);
      if (multiChoice) {
        values.push(multiChoice.value);
        optionsIds.push(metadataId);
      }
    });
    const refactorValue = values.length ? values : null
    return {optionsIds, values: refactorValue};
  }

  /**
   * toggle the class
   * @param selector 
   * @param className 
   */
  toggleClass(selector: HTMLElement, className: string, callback?: (hasClass: boolean) => void) {
    if (selector) {
      const hasClass = selector.classList.contains(className);
      if (hasClass) {
        this.renderer.removeClass(selector, className);
      } else {
        this.renderer.addClass(selector, className);
      }
      if (callback) {
        callback(!hasClass)
      }
    }
  }

  /**
   * reassign the fields when create a new document or save as template
   * @param template_roles 
   * @param fields_data 
   * @returns 
   */
  async reassignTheFields(template_roles: RecipientRole[], fields_data) {
    template_roles.forEach((role: RecipientRole) => {
      for (const field in fields_data) {
        const { selected_field_role_id, selected_for_role_id } = fields_data[field];
        const roleId = String(role.id);
        if (selected_field_role_id && selected_field_role_id == role.old_id) {
          fields_data[field] = { ...fields_data[field], ...{ selected_field_role_id: roleId } }
        }
        if (selected_for_role_id && selected_for_role_id == role.old_id) {
          fields_data[field] = { ...fields_data[field], ...{ selected_for_role_id: Number(roleId) } }
        }
      }
      role.personfields.forEach((person) => {
        for (const field in fields_data) {
          const { selected_field_type_id, field_section } = fields_data[field];
          const personId = String(person.id);
          if (selected_field_type_id == person.old_id && field_section == "person_field") {
            fields_data[field] = { ...fields_data[field], ...{ selected_field_type_id: personId, prefixid: personId } }
          }
        }
      });
      role.orgfields.forEach((org) => {
        for (const field in fields_data) {
          const { selected_field_type_id, field_section } = fields_data[field];
          const orgId = String(org.id);
          if (selected_field_type_id == org.old_id && field_section == "org_field") {
            fields_data[field] = { ...fields_data[field], ...{ selected_field_type_id: orgId, prefixid: orgId } }
          }
        }
      });
      for (const blockUuid in fields_data['blocks']) {
        if (Object.prototype.hasOwnProperty.call(fields_data['blocks'], blockUuid)) {
          const block: Block = fields_data['blocks'][blockUuid];
          if ((["attachment", "upload-pdf"].includes(block.type)) && block.request_from == role.old_id) {
            fields_data['blocks'][blockUuid] = { ...fields_data['blocks'][blockUuid], ...{ request_from: role.id } }
          }
        }
      }
    });
    return fields_data;
  }

  /**
   * get default currency
   */
  getSystemCurrency() {
    const defaultWorkSpace = this.getDefaultWorkSpace();
    return defaultWorkSpace?.company?.system_currency ?? constants.default_currency;
  }





  /**
   * Searches for a field within the provided fields object that matches the specified key-value pair and section.
   *
   * @param fields - An object containing fields to search through.
   * @param key - The property name to match against within each field.
   * @param value - The value to match for the specified key.
   * @param section - The section name to match within each field's `field_section` property.
   * @returns The first field that matches the given key-value pair and section, or `undefined` if no match is found.
   */
  findField(fields, key: string, value: string | number, section: string): Field {
    return Object.values(fields).find(
      (field: Field) => field.field_section == section && field[key] == value
    );
  }



  /**
   * find the fields with condition
   * @param fields - An object containing fields to search through.
   * @param key - The property name to match against within each field.
   * @param value - The value to match for the specified key.
   * @param section - The section name to match within each field's `field_section` property.
   * @returns The fields that match the given key-value pair and section, or `undefined` if no match is found.
   */
  findFieldsWithCondition(fields, key: string, value: string | number, section: string): Field[] {
    return Object.values(fields).filter(
      (field: Field) => field.field_section == section && field[key] == value && field.semi_hidden_field === 0 && field.secret_hidden_field === 0
    );
  }





  /**
   * find the matching field
   * @param fields 
   * @param key 
   * @param value 
   * @param section 
   * @returns 
   */
  findMatchingField(fields, key: string, value: number | string, section: string): Field[] {
    return Object.values(fields).filter(
      (field: Field) => field.field_section == section && field[key] == value
    );
  }

  commaSeparatedEmailsValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: string | boolean } | null => {
      if (!control.value) {
        return null; // Don't validate empty values to allow required validator to handle them
      }

      const emails = control.value.split(',');
      const emailPattern = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;

      for (const email of emails) {
        if (!emailPattern.test(email.trim())) {
          return { 'invalidEmails': true };
        }
      }

      return null;
    };
  }

  /**
   * return short lang value
   * @param selectedLang 
   * @returns 
   */
  shortLangValue(selectedLang: string): string | undefined {
    const langMap: Record<string, string> = {
      danish: "da",
      english: "en",
      finnish: "fi",
      french: "fr",
      german: "de",
      norwegian: "no",
      spanish: "es",
      swedish: "sv"
    };
    return langMap[selectedLang];
  }




  /**
   * open the bankid app
   * @param autoStartToken 
   */
  async openBankIdApp(autoStartToken: string, isMobile: boolean, OS: string) {
    if (isMobile) {
      if (OS == "Android") {
        if(this.deviceService.browser.toLowerCase() === 'samsung' || this.deviceService.userAgent.includes("SamsungBrowser")){
          window.location.href = `https://app.bankid.com/?autostarttoken=${autoStartToken}&redirect=${null}`;
        }else{
          window.open(`https://app.bankid.com/?autostarttoken=${autoStartToken}&redirect=${null}`, '_blank');
        }
      } else {
        window.location.href = `https://app.bankid.com/?autostarttoken=${autoStartToken}&redirect=${null}`;
      }
    } else {
      window.open(`bankid:///?autostarttoken=${autoStartToken}&redirect=${null}`, '_blank');
    }
  }

   isSamsungBrowser(): boolean {
    try {
      const browserName = (this.deviceService.browser || '').toString().toLowerCase();
      const ua = this.deviceService.userAgent || '';
      return browserName === 'samsung' || /SamsungBrowser/i.test(ua) || (/Samsung/i.test(ua) && /Android/i.test(ua));
    } catch (err) {
      return false;
    }
  }

  /**
   * Filter the duplicate fields
   * @param fields 
   * @returns 
   */
  filterDuplicates = (fields: Data) => {
    const uniqueFields: { [key: string | number]: Field } = {};
    const seenSelectedFieldTypeIds: Set<number> = new Set();

    for (const key in fields) {
      if (key === "blocks") continue;

      const field = fields[key];
      const fieldTypeId = Number(field.selected_field_type_id);

      if (!seenSelectedFieldTypeIds.has(fieldTypeId) || fieldTypeId === 0) {
        uniqueFields[key] = field;
        seenSelectedFieldTypeIds.add(fieldTypeId);
      } else {
        const existingKey = Object.keys(uniqueFields).find(
          k => Number(uniqueFields[k].selected_field_type_id) === fieldTypeId
        );

        if (existingKey) {
          const existingField = uniqueFields[existingKey];

          if (
            (field.pinned_field === 1 || field.semi_hidden_field === 1) &&
            existingField.pinned_field !== 1 &&
            existingField.semi_hidden_field !== 1
          ) {
            // replace with pinned/semi_hidden one
            delete uniqueFields[existingKey];
            uniqueFields[key] = field;
          }
        }
      }
    }

    return uniqueFields;
  };

  // File validation function
  validateFileSize(file: File): boolean {
    if (file.size > this.MAX_FILE_SIZE) {
      console.warn(`File ${file.name} exceeds the 20 MB limit: ${file.size / (1024 * 1024)} MB`);
      return false;
    }
    return true;
  }




  get getFieldTypeEnums(): Array<FieldTypeEnum> {
    return [FieldTypeEnum.Checkbox, FieldTypeEnum.Datepicker, FieldTypeEnum.Droplist, FieldTypeEnum.List, FieldTypeEnum.Signature, FieldTypeEnum.Text];
  }



  /**
   * Calculates and returns the next available field order number.
   *
   * Iterates through the `fields_data` object, filters fields whose type is included in `getFieldTypeEnums`,
   * and collects their `order` values. Returns one greater than the current maximum order, or `0` if no such fields exist.
   *
   * @returns {number} The next available order number for a field, or `0` if no fields are present.
   */
  public getMaxFieldOrder(): number {
    const orders = Object.values(this.fields_data)
      .filter((f: Field) =>
        this.getFieldTypeEnums.includes(f?.type as FieldTypeEnum)
      )
      .map((f: Field) => f.order ?? 0);

    if (!orders.length) {
      return 0;
    }

    return Math.max(...orders) + 1;
  }


  isValidUrl() {
    return new RegExp(
      '(https?://(?:www\\.|(?!www))[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\\.[^\\s]{2,}|www\\.[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\\.[^\\s]{2,}|https?://(?:www\\.|(?!www))[a-zA-Z0-9]+\\.[^\\s]{2,}|www\\.[a-zA-Z0-9]+\\.[^\\s]{2,})'
    )
  }



  /**
   * sanitize the html content
   * @param content 
   * @returns 
   */
  sanitizeContent(content: string): SafeHtml {
    return this._sanitizer.sanitize(SecurityContext.HTML, this._sanitizer.bypassSecurityTrustHtml(content));
  }

  ngOnDestroy() {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }
  }

  parseJwtData(token) {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(function (c) {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));

    return JSON.parse(jsonPayload);
  }

  criiptoBankIDSuccess(data) {
    const bankDetails = this.parseJwtData(data.id_token);
    let signedBypersonnr = '';
    if (bankDetails.identityscheme) {
      if (bankDetails.identityscheme == "dknemid") {
        signedBypersonnr = bankDetails.cprNumberIdentifier;
      } else if (bankDetails.identityscheme == "dkmitid") {
        signedBypersonnr = bankDetails.cprNumberIdentifier;
      } else if (bankDetails.identityscheme == "nobankid-oidc") {
        signedBypersonnr = typeof bankDetails.socialno != "undefined" ? bankDetails.socialno : "";
      } else if (bankDetails.identityscheme == "fitupas") {
        if (bankDetails.hetu != "") {
          signedBypersonnr = bankDetails.hetu;
        } else {
          signedBypersonnr = bankDetails.satu;
        }
        signedBypersonnr = signedBypersonnr.replace(/-/g, "")
      } else if (bankDetails.identityscheme == "sebankid") {
        signedBypersonnr = typeof bankDetails.ssn != "undefined" ? bankDetails.ssn : "";
      }
    }

    return {
      response: bankDetails,
      personal_number: signedBypersonnr
    }
  }


  /**
   * Retrieves the HTML content of a specified selector, processes elements with the class `.tiptap`,
   * and replaces their content with the corresponding editor instance's DOM content if available.
   *
   * @param selectorClass - The CSS class selector used to locate the target element in the DOM.
   * @returns The processed HTML content as a string.
   *
   * @remarks
   * - This method assumes that elements with the class `.tiptap` have a `uuid` attribute
   *   that corresponds to an editor instance in the `editorInstances` object.
   * - If an editor instance is found for a given `uuid`, its DOM content is used to replace
   *   the `.tiptap` element's content. Otherwise, the original `.tiptap` element's content is retained.
   * - The method creates a temporary `div` element to manipulate the DOM structure before returning the result.
   */
  getTipTapContent(selectorClass: string) {
    document.querySelectorAll('.field-invalid').forEach((invalidField: HTMLElement) => {
      this.renderer.removeClass(invalidField, 'field-invalid');
    });
    const content = document.createElement('div') as HTMLElement;
    content.innerHTML = document.querySelector(selectorClass).innerHTML;
    content.querySelectorAll('.tiptap').forEach((editor: HTMLElement) => {
      const uuid = editor.getAttribute('uuid');
      const editorParentElement = content.querySelector(`.editor${uuid}`);
      let html = '';
      if (Object.prototype.hasOwnProperty.call(this.editorInstances, uuid) && this.editorInstances[uuid]) {
        html = this.editorInstances[uuid].view.dom.innerHTML;
      } else {
        html = editor.innerHTML;
      }
      editorParentElement.innerHTML = html;
    });

    return content.innerHTML;
  }

  /**
   *  Auto height for textarea 
   * @param element 
   */
  autoHeightForTextarea(element:HTMLElement) {
    if(element){
      element.style.height = (element.scrollHeight) + "px";
    }
  }

  onDropOnInnerIndicator(event, element: HTMLElement) {
    const dropEl = event as HTMLElement;
    const targetInner = dropEl.closest('.content-box-inner') as HTMLElement;
    const currentRowItem = targetInner.closest('.rowItem') as HTMLElement;

    const parentEl = currentRowItem.parentElement;
    if (!parentEl) {
        return;
    }
    const nextSibling = currentRowItem.nextSibling;

    const isAlreadyChild = currentRowItem.classList.contains('merged-row-item');
    const isAlreadyWrapped = currentRowItem.querySelector('.merged-row-item') !== null;

    const newChild = element.querySelector('.rowItem') as HTMLElement;
    if (isAlreadyChild || isAlreadyWrapped) {
        newChild.classList.add('merged-row-item');
        newChild.style.width = '100%';
        parentEl.insertBefore(newChild, nextSibling);
        return;
    }

    const newWrapper = document.createElement('div');
    newWrapper.classList.add('position-relative', 'merged-row');

    // convert current to child
    currentRowItem.classList.add('merged-row-item');
    currentRowItem.style.width = '100%';

    newChild.classList.add('merged-row-item');
    newChild.style.width = '100%';

    // wrap both
    newWrapper.appendChild(currentRowItem);
    newWrapper.appendChild(newChild);

    // insert wrapper in the original place
    parentEl.insertBefore(newWrapper, nextSibling);
  }

  findDroplistById(uuid: string, droplistId: string|number): DroplistOptions | undefined {
    const fieldsData: Field = this.fields_data[uuid];
    return fieldsData.options.find(droplist => droplist.id == droplistId);
  }

  refactorDroplistValue(value:string[], fieldData: Field) {
    const selectedValue: string[] = [];
    const selectedOptionid: number[] = [];
    const selectedNames: string[] = [];
    fieldData.options.forEach(option => {
        if (value.includes(option.value) || value.includes(option.name)) {
            selectedOptionid.push(option.id);
            selectedValue.push(option.value);
            selectedNames.push(option.name);
        }
    });

    return { selectedOptionid, selectedValue, selectedNames };
  }

  getFieldNameByType(selected_field: SelectedFieldEnum): string {    
    return this._translocoService.translate(this.fieldMap[selected_field]);
  }

  assignSignatureToRole(role: SelectedRole, uuid: string|number) {
    const sessionImageService = AppInjector.get(SessionImageService);
    // Find the element inside this component by uuid
    const currentElement = document.querySelector(
      `.drag-fields[uuid="${uuid}"]`
    );
    if (!currentElement || !currentElement.querySelector('.signature-image')) return {}; // safety check

    const selectedLabel = currentElement.querySelector('.selected-label');
    const signatureImage = currentElement.querySelector('.signature-image');
    const assignee = currentElement.querySelector('.assignee');

    let fieldUpdates = {};

    if (role.id === 0) {
      if (!this.user_settings.signature) {
        const domHelperService = AppInjector.get(DomHelperService);
        // Show selected-label
        this.renderer.removeClass(selectedLabel, 'hidden');
        this.renderer.addClass(selectedLabel, 'generate-signature');
        domHelperService.initializeEventListener('.generate-signature', 'click', 'generateSignatureForSender');
      } else {
        const signatureUrl = sessionImageService.getSignatureImages();
        this.renderer.addClass(selectedLabel, 'hidden');
        this.renderer.removeClass(selectedLabel, 'generate-signature');
        // Apply signature background
        this.renderer.setStyle(signatureImage, 'background-image', `url(${signatureUrl})`);

        fieldUpdates = {
          value: this.user_settings?.signature
        };
      }

      // Update assignee name
      this.renderer.setProperty(assignee, 'textContent', this.user_settings.name);

      // Show signature image
      this.renderer.removeClass(signatureImage, 'hidden');
    } else {
      // For non-zero role id
      this.renderer.removeClass(selectedLabel, 'hidden');

      // Update assignee name (prefer recipient over role name)
      const name = !role.recipient_id ? role.name : role.recipient;
      this.renderer.setProperty(assignee, 'textContent', name);

      // Reset signature image
      this.renderer.setStyle(signatureImage, 'background-image', 'url()');
      this.renderer.addClass(signatureImage, 'hidden');

      fieldUpdates = { 
        value: '',
        selected_for_role_id: role.id,
        recipient_id : role.recipient_id  
      };
    }

    return fieldUpdates;
  }

  /**
   * This function will convert contact json to form data
   * @param formValue The form value object.
   * @returns A FormData object.
   */
  contactFormData(formValue): FormData {
    const formData = new FormData();
    for (const key of Object.keys(formValue)) {
      const value = formValue[key];
      if(key === 'custom_fields'){
        for (const innerKey of Object.keys(value)) {
          const customField = value[innerKey];
          if(customField.field_type === 'text' || (customField.field_type === 'image' && customField.value instanceof File)){
            formData.append(`custom_fields[${innerKey}][meta_data_id]`, customField.meta_data_id);
            formData.append(`custom_fields[${innerKey}][value]`, customField.value);
            formData.append(`custom_fields[${innerKey}][field_type]`, customField.field_type);
          }
        }
      } else {
        if (value === null) {
          formData.append(key, '');
        } else if (Array.isArray(value)) {
          value.forEach((val) => {
            formData.append(`${key}[]`, val);
          });
        } else {
          formData.append(key, value);
        }
      }
    }
    return formData;
  }

  /**
   * Get value from custom field
   * @param customField The custom field object.
   * @returns The value of the custom field.
   */
  getContactCustomFieldValue(customField: ICustomFieldDto): string {
    if(customField?.field_type === 'text'){
      return customField?.value || '';
    } else if(customField?.field_type === 'image'){
      return customField?.file_name || '';
    } 
    return '';
  }
   
  destroyTiptap(){
    for (const editor in this.editorInstances) {
      if (this.editorInstances[editor]) {
        this.editorInstances[editor]?.destroy();
        delete this.editorInstances[editor];
      }
    }
  }

  skipCustomFieldsFromRoles(rolesData: RecipientRole[]): RecipientRole[] {
    return rolesData.map(role => {
      const newRole = { ...role };

      if (newRole.recipient?.custom_fields) {
        const { custom_fields, ...recipient } = newRole.recipient;
        newRole.recipient = recipient;
      }

      if (newRole.organization?.custom_fields) {
        const { custom_fields, ...organization } = newRole.organization;
        newRole.organization = organization;
      }

      return newRole;
    });
  }

  updateUploadedAttachmentsStatus() {
    const updatedBlocks = { ...this.fields_data['blocks'] };
    for (const block of Object.values(updatedBlocks) as Block[]) {
      const isAttachment = block?.type === 'attachment';
      const isUploaded = block?.is_uploaded === 1;
      const hasValidMeta = (block?.meta_data_id ?? 0) !== 0;
      const hasValidRole = (block?.role_id ?? 0) !== 0;

      if (isAttachment && isUploaded && hasValidMeta && hasValidRole) {
        updatedBlocks[block.uuid] = { ...block, is_uploaded: 0 };
      }
    }
    this.fields_data = { ...this.fields_data, blocks: updatedBlocks };
  }

  updateTheSigningPageHeightOnImageLoad() {
    const MOBILE_BREAKPOINT = 911;
    if (window.outerWidth > MOBILE_BREAKPOINT) {
      return;
    }

    const templatePageContainer = document.querySelector('.template-main-container');
    const templatePageHeight = document.querySelector('.template-page')?.getBoundingClientRect()?.height;

    if (templatePageContainer && templatePageHeight != null) {
      this.renderer.setStyle(templatePageContainer, 'height', `${templatePageHeight}px`);
    }
  }
}
